import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Reply } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reply-notifications`;
const ago = (iso) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

// CRM bell: new replies to 1:1 emails. Senders see their own, admins see everyone's.
export default function ReplyBell({ testid = 'reply-bell' }) {
  const [d, setD] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = useCallback(() => axios.get(API).then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const openOne = async (n) => {
    setOpen(false);
    if (n.unread) await axios.post(`${API}/${n.id}/read`).catch(() => {});
    load();
    nav(`/admin/contacts?open=${n.contact_id}`);
  };
  const readAll = async () => { await axios.post(`${API}/read-all`).catch(() => {}); load(); };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full bg-white border border-gray-200 shadow-sm text-gray-600 hover:text-gray-900 hover:border-[#00A79D] transition-colors" data-testid={testid} aria-label="Replies">
          <Bell className="w-5 h-5" />
          {d.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F5871F] text-[#ffffff] text-[10px] font-bold flex items-center justify-center" data-testid={`${testid}-count`}>{d.unread > 99 ? '99+' : d.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid={`${testid}-panel`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">Replies</p>
          {d.unread > 0 && <button onClick={readAll} className="text-xs text-[#00857D] hover:underline" data-testid={`${testid}-read-all`}>Mark all read</button>}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {d.items.length === 0 && <p className="px-4 py-8 text-center text-sm text-gray-400" data-testid={`${testid}-empty`}>No replies yet. When someone answers a 1:1 email, it shows here.</p>}
          {d.items.map((n) => (
            <button key={n.id} onClick={() => openOne(n)} className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 flex gap-3 ${n.unread ? 'bg-[#f0fbfa]' : ''}`} data-testid={`reply-notification-${n.id}`}>
              <Reply className={`w-4 h-4 mt-0.5 shrink-0 ${n.unread ? 'text-[#00A79D]' : 'text-gray-300'}`} />
              <span className="min-w-0 flex-1">
                <span className="flex justify-between gap-2"><span className={`text-sm truncate ${n.unread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{n.contact_name}</span><span className="text-[10px] text-gray-400 shrink-0">{ago(n.created_at)}</span></span>
                <span className="block text-xs text-gray-600 truncate">{n.subject}</span>
                {n.snippet && <span className="block text-xs text-gray-400 truncate">{n.snippet}</span>}
              </span>
            </button>))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
